import React from 'react';
import { DUE_SOON_DAYS } from '../utils/dueStatus';

/**
 * Small, accessible overdue / due-soon dot — the shared row affordance used on
 * the Gag Breakdown gag rows and the shared task board (list rows + kanban
 * cards). Driven by the `dueStatus()` helper's output so the colour + meaning
 * stay identical everywhere.
 *
 * Not colour alone: the dot carries role="img" + an aria-label AND a title, so
 * it reads for screen-reader and hover users (the a11y pass this section just
 * had). Renders nothing for a null/unknown state.
 *
 * 🔑 **THAT ACCESSIBLE NAME BECAME LOAD-BEARING ON 2026-08-08 — do not strip it
 * to "tidy up a decorative dot".** The Gag Breakdown's shoot-date TEXT used to
 * repeat the signal in red/amber bold; the owner reduced it to the dot alone
 * (*"actualy how aobut jst keeping the colour dot?"*), so on that surface this
 * `aria-label`/`title` pair is now the ONLY non-colour statement of "overdue" /
 * "due soon". Colour-blind and screen-reader users have nothing else.
 *
 * ♿ **AND THE LIGHT AMBER DOT WAS STRENGTHENED IN THE SAME CHANGE, 500 → 600 —
 * a MEASURED fix, not a taste change.** Once the dot is the sole carrier,
 * WCAG 1.4.11 (non-text contrast, 3:1) applies to the dot itself. Computed
 * against the surfaces these actually sit on, with `index.css` checked for
 * `!important` overrides on `background-color` first (there are none for any of
 * these classes, so the declared colour is the enforced one):
 *
 *   light, on #ffffff   amber-500 #f59e0b → **2.15:1  ✗ FAILS**
 *                       amber-600 #d97706 → **3.19:1  ✓**
 *                       red-500   #ef4444 → 3.76:1  ✓ (unchanged)
 *   dark,  on #1f2937   amber-400 #fbbf24 → 8.79:1  ✓ (unchanged)
 *                       red-400   #f87171 → 5.31:1  ✓ (unchanged)
 *
 * ⚠️ **This is a SHARED component — the change reaches `TaskCard.js` and
 * `TaskListView.js` too**, where the same dot marks task due dates. That is
 * deliberate: 2.15:1 was below the non-text minimum on those surfaces as well,
 * so this fixes a real failure everywhere rather than only where it was noticed.
 * 🪤 It could NOT have been done by passing a `className` override from the one
 * caller — two conflicting Tailwind background utilities on one element resolve
 * by STYLESHEET order, not by the order they were written, which is the same
 * trap `ui/Toolbar`'s `marginClassName` prop exists to avoid.
 * 🔴 Only the LIGHT amber moved. The dark amber and both reds already cleared
 * 3:1 and were left alone — a blanket "darken them all" would have changed
 * three colours to fix one.
 *
 * 🔀 **`titleOnParent` (2026-08-08) — THE DOT MAY GIVE UP ITS `title`, AND ONLY
 * ITS `title`.** Product owner, looking at the Breakdown: *"dot tool tips like
 * the amber dots have a tool tip but you have to hover over the dot. please make
 * it the whole cell that has the same tool tip"*. A 8×8px hover target is a poor
 * one, so the tooltip moves up to the containing cell — and it can only move,
 * never be duplicated, because **a native `title` on a CHILD wins over its
 * parent's**. Leaving it here would show the dot's string on the dot and the
 * cell's string everywhere else, i.e. two tooltips again, which is the complaint
 * rather than the fix. This is the same resolution `components/LeadTimeRisk.js`'s
 * `LeadTimeDot` reached hours earlier for the order table's Date cell; that one
 * dropped its `title` outright because it has exactly one caller, whereas this
 * component is shared by four surfaces and only ONE of them is a table cell.
 *
 * 🔑 **DEFAULT `false`, so every caller that does not opt in is byte-identical.**
 * ♿ **`role="img"` and `aria-label` are NOT optional and there is deliberately no
 * prop to remove them.** They are the non-colour channel, and on the Breakdown
 * this component is the SOLE carrier — the rule at the top of this docblock. What
 * moves is the MOUSE affordance; the assistive-technology channel is untouched,
 * and the parent's tooltip must say at least what this one said (see
 * `dueIndicatorTitle`, which exists so the two cannot drift).
 * ⚠️ **A caller passing `titleOnParent` OWES its container a tooltip.** Set it
 * without one and hover users lose the signal entirely, with colour left as the
 * only channel for them — do not pass it "to tidy up an attribute".
 *
 * @param {'overdue'|'due_soon'|null} props.state  from dueStatus().
 * @param {string} [props.className]
 * @param {boolean} [props.titleOnParent=false]  omit the dot's own `title`
 *   because the containing element carries the same string. Nothing else changes.
 */
const DUE_CONFIG = {
  overdue: {
    dot: 'bg-red-500 dark:bg-red-400',
    text: 'Overdue',
    title: 'Overdue — due date has passed',
  },
  due_soon: {
    // 600 in light, not 500 — see the contrast table above. 3.19:1 vs 2.15:1.
    dot: 'bg-amber-600 dark:bg-amber-400',
    text: 'Due soon',
    title: `Due soon — within ${DUE_SOON_DAYS} days`,
  },
};

/**
 * THE tooltip sentence for a due state — the string a parent must use when it
 * takes the tooltip off the dot via `titleOnParent`.
 *
 * 🔑 **It exists so the cell and the dot cannot drift.** The alternative is a
 * caller retyping "Overdue — due date has passed", which is the Code Unification
 * Principle's first failure mode and is how "the same tooltip" quietly stops
 * being the same tooltip. `DUE_CONFIG` stays private; this is the one export.
 *
 * @param {'overdue'|'due_soon'|null} state  from dueStatus().
 * @returns {string|undefined} `undefined` for a state with no dot, so a caller
 *   can spread it straight into `title=` and get NO attribute rather than an
 *   empty one that renders a blank tooltip box (the `buildOrderDateTooltip`
 *   convention).
 */
export function dueIndicatorTitle(state) {
  return DUE_CONFIG[state]?.title;
}

export default function DueIndicator({ state, className = '', titleOnParent = false }) {
  const cfg = DUE_CONFIG[state];
  if (!cfg) return null;
  return (
    <span
      role="img"
      aria-label={cfg.text}
      // `undefined` renders NO attribute — see `titleOnParent` in the docblock.
      // The accessible name above is deliberately NOT conditional.
      title={titleOnParent ? undefined : cfg.title}
      className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot} ${className}`.trim()}
    />
  );
}
